import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const RandomContactCard = ({ 
  contact, 
  onSendMessage, 
  onViewDetails, 
  onSkip,
  isLoading = false 
}) => {
  if (!contact) {
    return (
      <div className="bg-surface border border-border rounded-xl p-8 text-center shadow-card">
        <div className="w-24 h-24 bg-muted rounded-full mx-auto mb-4 flex items-center justify-center">
          <Icon name="User" size={32} className="text-text-secondary" />
        </div>
        <p className="text-text-secondary">Click "Get Random Contact" to start</p>
      </div>
    );
  }

  const getDaysSinceLastContact = () => {
    if (!contact?.lastContactDate) return 'Never contacted';
    const days = Math.floor((new Date() - new Date(contact.lastContactDate)) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    if (days < 30) return `${days} days ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return `${Math.floor(days / 365)} years ago`;
  };

  const getContactPriority = () => {
    if (!contact?.lastContactDate) return 'high';
    const days = Math.floor((new Date() - new Date(contact.lastContactDate)) / (1000 * 60 * 60 * 24));
    if (days > 90) return 'high';
    if (days > 30) return 'medium';
    return 'low';
  };

  const priority = getContactPriority();
  const priorityColors = {
    high: 'bg-error text-error-foreground',
    medium: 'bg-warning text-warning-foreground',
    low: 'bg-success text-success-foreground'
  };

  if (isLoading) {
    return (
      <div className="bg-surface border border-border rounded-xl p-8 text-center shadow-card animate-pulse">
        <div className="w-24 h-24 bg-muted rounded-full mx-auto mb-4"></div>
        <div className="h-6 bg-muted rounded mb-2 w-32 mx-auto"></div>
        <div className="h-4 bg-muted rounded mb-4 w-24 mx-auto"></div>
        <div className="space-y-2">
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-10 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-xl p-6 lg:p-8 shadow-card hover-lift transition-micro">
      {/* Contact Header */}
      <div className="text-center mb-6">
        <div className="relative inline-block mb-4">
          <div className="w-24 h-24 lg:w-32 lg:h-32 rounded-full overflow-hidden mx-auto shadow-card">
            {contact?.avatar ? (
              <Image 
                src={contact?.avatar} 
                alt={contact?.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <Icon name="User" size={32} className="text-text-secondary" />
              </div>
            )}
          </div>
          
          {/* Priority Badge */}
          <div className={`absolute -top-2 -right-2 px-2 py-1 rounded-full text-xs font-medium ${priorityColors?.[priority]}`}>
            {priority === 'high' && 'High Priority'}
            {priority === 'medium' && 'Medium'}
            {priority === 'low' && 'Recent'}
          </div>
        </div>

        <h2 className="text-xl lg:text-2xl font-semibold text-text-primary mb-2">
          {contact?.name}
        </h2>
        
        <div className="flex items-center justify-center space-x-2 text-text-secondary mb-2">
          <Icon name="Clock" size={16} />
          <span className="text-sm">Last contact: {getDaysSinceLastContact()}</span>
        </div>

        {contact?.company && (
          <div className="flex items-center justify-center space-x-2 text-text-secondary mb-2">
            <Icon name="Building" size={16} />
            <span className="text-sm">{contact?.company}</span>
          </div>
        )}

        {contact?.category && (
          <span className="inline-block px-3 py-1 bg-muted text-text-secondary text-xs rounded-full">
            {contact?.category}
          </span>
        )}
      </div>
      {/* Contact Notes */}
      {contact?.notes && (
        <div className="bg-muted rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-2">
            <Icon name="FileText" size={16} className="text-text-secondary mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-text-primary mb-1">Notes:</p>
              <p className="text-sm text-text-secondary leading-relaxed">
                {contact?.notes}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Quick Actions */}
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Button
            variant="outline"
            onClick={() => onSendMessage('sms')}
            iconName="MessageSquare"
            iconPosition="left"
            className="justify-center"
          >
            SMS
          </Button>
          
          <Button
            variant="outline"
            onClick={() => onSendMessage('email')}
            iconName="Mail"
            iconPosition="left"
            className="justify-center"
          >
            Email
          </Button>
          
          <Button
            variant="outline"
            onClick={() => onSendMessage('whatsapp')}
            iconName="MessageCircle"
            iconPosition="left"
            className="justify-center"
          >
            WhatsApp
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            variant="default"
            onClick={onViewDetails}
            iconName="User"
            iconPosition="left"
            className="justify-center"
          >
            View Details
          </Button>
          
          <Button
            variant="ghost"
            onClick={onSkip}
            iconName="SkipForward"
            iconPosition="left"
            className="justify-center"
          >
            Skip Contact
          </Button>
        </div>
      </div>
      {/* Contact Info */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          {contact?.email && (
            <div className="flex items-center space-x-2">
              <Icon name="Mail" size={16} className="text-text-secondary" />
              <span className="text-text-secondary truncate">{contact?.email}</span>
            </div>
          )}
          
          {contact?.phone && (
            <div className="flex items-center space-x-2">
              <Icon name="Phone" size={16} className="text-text-secondary" />
              <span className="text-text-secondary">{contact?.phone}</span>
            </div>
          )}
          
          {contact?.location && (
            <div className="flex items-center space-x-2">
              <Icon name="MapPin" size={16} className="text-text-secondary" />
              <span className="text-text-secondary truncate">{contact?.location}</span>
            </div>
          )}
          
          {contact?.birthday && (
            <div className="flex items-center space-x-2">
              <Icon name="Calendar" size={16} className="text-text-secondary" />
              <span className="text-text-secondary">{contact?.birthday}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RandomContactCard;